"use client"
import { useColorScheme } from '@mui/material/styles'
import { useCallback } from 'react'
import { setCookie } from '@/lib/libs'

/**
 * Hook personnalisé pour la gestion du thème
 * Utilise useColorScheme de Material-UI pour une gestion standardisée
 */
export const useThemeToggle = () => {
  const { mode, setMode, setColorScheme } = useColorScheme()

  /**
   * Obtient le thème effectif (en tenant compte du mode système)
   */
  const getEffectiveTheme = useCallback(() => {
    // Vérifier d'abord le localStorage
    const savedMode = localStorage.getItem('mui-mode') as 'light' | 'dark' | 'system' | null
    
    if (savedMode && savedMode !== 'system') {
      const themeColor = savedMode === 'light' ? '#ffffff' : '#000000'

      // Vérifier et mettre à jour la meta tag theme-color seulement si nécessaire
      const metaThemeColor = document.querySelector('meta[name="theme-color"]')
      if (metaThemeColor && metaThemeColor.getAttribute('content') !== themeColor) {
        metaThemeColor.setAttribute('content', themeColor)
      }
      
      // Vérifier et mettre à jour la meta tag color-scheme seulement si nécessaire
      const metaColorScheme = document.querySelector('meta[name="color-scheme"]')
      if (metaColorScheme) {
        if (metaColorScheme.getAttribute('content') !== savedMode) {
          metaColorScheme.setAttribute('content', savedMode)
        }
      } else {
        // Créer la meta tag si elle n'existe pas
        const newMetaColorScheme = document.createElement('meta')
        newMetaColorScheme.name = 'color-scheme'
        newMetaColorScheme.content = savedMode
        document.head.appendChild(newMetaColorScheme)
      }

      return savedMode
    }
    
    if (!savedMode || savedMode === 'system') {
      // Vérifie si l'élément HTML a la classe theme-dark
      // const htmlElement = document.documentElement
      // const hasDarkClass = htmlElement.classList.contains('theme-dark')
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      
      return prefersDark ? 'dark' : 'light'
    }

    return savedMode
  }, [])

  /**
   * Bascule entre les modes light, dark et system
   */
  const toggleTheme = useCallback(() => {
    let newMode: 'light' | 'dark' // | 'system'
    
    if (mode === 'light') {
      newMode = 'dark'
    } else if (mode === 'dark') {
      newMode = 'light'
    } else if (mode === 'system') {
      newMode = getEffectiveTheme() as 'light' | 'dark'
    }
    
    setMode(newMode)
    setColorScheme(newMode)
    
    // Mettre à jour le cookie et les attributs comme dans l'ancienne fonction
    const themeColor = newMode === 'light' ? '#ffffff' : '#000000'

    setCookie('mui-mode', newMode)
    localStorage.setItem('mui-mode', newMode)
    
    // Mettre à jour la meta tag theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColor)
    }
    
    // Mettre à jour la meta tag color-scheme
    const metaColorScheme = document.querySelector('meta[name="color-scheme"]')
    if (metaColorScheme) {
      metaColorScheme.setAttribute('content', newMode)
    } else {
      // Créer la meta tag si elle n'existe pas
      const newMetaColorScheme = document.createElement('meta')
      newMetaColorScheme.name = 'color-scheme'
      newMetaColorScheme.content = newMode
      document.head.appendChild(newMetaColorScheme)
    }
  }, [getEffectiveTheme, mode, setColorScheme, setMode])

  /**
   * Définit directement le mode du thème
   */
  const setThemeMode = useCallback((newMode: 'light' | 'dark' | 'system') => {
    setMode(newMode)

    if (newMode === 'system') {
      setMode(null)
      newMode = getEffectiveTheme() as 'light' | 'dark'
    }
    
    setColorScheme(newMode as 'light' | 'dark')
    
    // Mettre à jour le cookie et les attributs
    const themeColor = newMode === 'light' ? '#ffffff' : '#000000'

    setCookie('mui-mode', newMode)
    localStorage.setItem('mui-mode', newMode)
      
    // Mettre à jour la meta tag theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColor)
    }
      
    // Mettre à jour la meta tag color-scheme
    const metaColorScheme = document.querySelector('meta[name="color-scheme"]')
    if (metaColorScheme) {
      metaColorScheme.setAttribute('content', newMode)
    } else {
      // Créer la meta tag si elle n'existe pas
      const newMetaColorScheme = document.createElement('meta')
      newMetaColorScheme.name = 'color-scheme'
      newMetaColorScheme.content = newMode
      document.head.appendChild(newMetaColorScheme)
    }
  }, [getEffectiveTheme, setColorScheme, setMode])

  /**
   * Vérifie si le thème actuel est en mode système
   */
  const isSystemMode = mode === 'system'

  return {
    mode,
    toggleTheme,
    setThemeMode,
    isSystemMode,
    getEffectiveTheme,
  }
}
